"use client";

import { motion } from "framer-motion";
import { SPORTS_LIST } from "@/config/sports";
import { UserAvatar } from "../ui/user-avatar";

// Deterministic seeded random to prevent Next.js SSR hydration mismatches
const seededRandom = (seed: number) => {
	const x = Math.sin(seed) * 1000;
	return x - Math.floor(x);
};

const pseudoRandomShuffle = (arr: typeof SPORTS_LIST) => {
	const shuffled = [...arr];
	let seed = 42; // Fixed seed guarantees SSR and Client render identically
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(seededRandom(seed++) * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
};

const SHUFFLED_SPORTS = pseudoRandomShuffle(SPORTS_LIST).filter(
	(sport) => !["kabaddi", "kho-kho"].includes(sport.id.toLowerCase()),
);

// Filter out sports that share the exact same icon (like fallback Activity/Ball icons)
// to ensure the marquee looks visually diverse
const UNIQUE_ICON_SPORTS = SHUFFLED_SPORTS.filter(
	(sport, index, self) =>
		index === self.findIndex((s) => s.icon === sport.icon),
);

// const POPULAR_SPORTS_IDS = [
// 	"football",
// 	"basketball",
// 	"tennis",
// 	"volleyball",
// 	"badminton",
// 	"baseball",
// 	"athletics",
// 	"swimming",
// 	"hockey",
// 	"golf",
// 	"boxing",
// 	"table tennis",
// ];

// const SHUFFLED_SPORTS = pseudoRandomShuffle(SPORTS_LIST).filter((sport) =>
// 	POPULAR_SPORTS_IDS.includes(sport.id.toLowerCase()),
// );

export function SportsMarquee() {
	// Duplicate the list so the marquee scroll is seamless
	const duplicatedSports = [...UNIQUE_ICON_SPORTS, ...UNIQUE_ICON_SPORTS];

	return (
		// <section className="relative w-full overflow-hidden border-y border-border/40 bg-muted/10 py-6 md:py-8 dark:bg-black/20">
		<section className="w-full border-border/40 border-y bg-muted/10 py-12 md:py-14 dark:bg-black/20">
			<div className="mb-11 px-4 text-center md:mb-14">
				<p className="font-mono font-semibold text-muted-foreground/80 text-xs uppercase tracking-[0.2em] md:text-sm">
					Supporting 30+ sports
				</p>
				<h2 className="mt-3 font-semibold text-3xl text-foreground/90 tracking-tight md:text-4xl">
					Built for every athlete
				</h2>
			</div>

			<div className="relative w-full overflow-hidden">
				{/* Left & Right gradient masks to blend the edges seamlessly */}
				<div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background to-transparent md:w-32" />
				<div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background to-transparent md:w-32" />

				<motion.div
					className="flex w-max items-center gap-6 pl-6 md:gap-8 md:pl-8 lg:gap-12 lg:pl-12"
					animate={{
						x: ["0%", "-50%"],
					}}
					transition={{
						repeat: Number.POSITIVE_INFINITY,
						ease: "linear",
						duration: 80,
					}}
				>
					{duplicatedSports.map((sport, idx) => {
						const SportIcon = sport.icon;
						// We use idx in the key because the list is duplicated
						return (
							<div
								key={`${sport.id}-${idx}`}
								className="group flex flex-shrink-0 cursor-default select-none items-center gap-2 rounded-full border border-border/40 bg-card/50 px-4 py-3 transition-colors hover:bg-card md:gap-3 md:px-5 md:py-3.5"
							>
								<div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-300 md:size-14">
									<SportIcon
										size={16}
										stroke={2}
										className="size-[22px] md:size-[26px]"
									/>
								</div>
								<span className="px-2 font-semibold text-base text-foreground/80 tracking-tight transition-colors group-hover:text-foreground md:px-3">
									{sport.label}
								</span>
							</div>
						);
					})}
				</motion.div>
			</div>
		</section>
	);
}
